// ARKit-52 -> N-control rig mapping (live lip-sync path).
//
// A splat character's rig is an N-element vector of MetaHuman board controls
// declared by its bundle's rig_names.json (isaac V1: 60, isaac V2 head+teeth:
// 174). Convorcher streams 52-element ARKit blendshape frames per sentence
// (the same A2F buffer LiveLink drives Myra's morph targets from — see
// AudioAnimationBuffer.getCurrentArkit). To drive the splat from that, we
// reuse the exact ARKit->188 MetaHuman mapping the rig2mesh model was trained
// with (the arkitToMh mapping in ./arkitToMh.js), then gather the N controls
// the character needs.
//
// The 188->N gather is derived at load time from the bundle's rig_names.json
// (./rigGatherFromNames.js — see OGSModel.setLipSyncGather). This module stays
// pure + dependency-light: the caller passes the gather in, which also keeps
// the function trivially unit-testable.

import { arkitToMh, MH_LEN } from './arkitToMh.js'

// Isaac V1's control-space size (legacy constant, kept for tests/tools).
export const ISAAC_RIG_DIM = 60

// Blink controls within Isaac V1's 60-vector, in rig_names.json order. Asserted
// against rig_names.json in tests/unit/isaac-rig-gather.test.mjs.
export const BLINK_L_SLOT = 9 // CTRL_L_eye_blink.translateY
export const BLINK_R_SLOT = 37 // CTRL_R_eye_blink.translateY

/**
 * Map a live 52-element ARKit frame to a character's N-element rig vector.
 *
 * ABOUT `rest`. ARKit-52 can only express a SUBSET of a MetaHuman board, and
 * arkitToMh ZEROES every control it does not write — so without a rest vector the
 * output is "the few controls ARKit reached, and 0 everywhere else". That is only
 * a neutral face if the control space is centred on 0. For one declaring
 * rig_range [0,1] it is an extreme corner the decoders never saw: measured on
 * eyeline V10-C, ARKit reaches 53 of 168 controls (115 are permanently 0) against
 * a trained mean with 133 non-zero, and the resulting vector sits FURTHER from
 * the training distribution (L2 3.32) than a real trained pose does (2.96). The
 * decoders then extrapolate, which is what a washed-out, detail-free face is.
 *
 * ARKit weights are deltas from neutral BY DEFINITION (0 = neutral, 1 = full
 * expression), and arkitToMh maps them into an MH space with the same convention
 * — `arkitToMh(zeros)` is all-zeros, verified. So ARKit's contribution is ADDED
 * to rest rather than replacing it: an ARKit-neutral frame reproduces `rest`
 * exactly, and the 115 controls ARKit cannot reach hold their rest value instead
 * of collapsing to 0.
 *
 * Passing no `rest` reproduces the previous behaviour exactly, which is correct
 * for a bundle whose controls do rest at zero.
 *
 * @param {ArrayLike<number>} arkit52 - live ARKit weights (length >= 52)
 * @param {number[]} gather - N indices into the 188 MH controls
 *                            (gatherFromRigNames on the bundle's rig_names.json)
 * @param {Float32Array} outN - destination, length N === gather.length (overwritten)
 * @param {Float32Array} [scratch188] - optional reusable 188-length buffer to
 *                            avoid a per-call allocation on the hot path
 * @param {ArrayLike<number>|null} [rest] - the bundle's rest configuration, length N.
 *                            Ignored when its length doesn't match the gather.
 * @param {[number,number]|Array<[number,number]>|null} [range] - either the
 *                            bundle's declared `rig_range` as one `[lo,hi]` pair,
 *                            or PER-CONTROL trained-domain limits, one `[lo,hi]`
 *                            per gathered control (`trainedLimits`, src/lib/rigBoard.js).
 *                            Prefer the latter — see below.
 * @returns {Float32Array} outN
 */
export function arkitToIsaacRig(arkit52, gather, outN, scratch188, rest = null, range = null) {
  const buf = scratch188 || new Float32Array(MH_LEN)
  arkitToMh(arkit52, buf)
  const base = rest && rest.length === gather.length ? rest : null
  if (!base) {
    for (let i = 0; i < gather.length; i++) outN[i] = buf[gather[i]]
    return outN
  }
  // Clamping matters because rest is already inside the domain: adding a full
  // expression on top can leave it, and a control outside its declared range is
  // extrapolation again — the very thing rest exists to avoid.
  //
  // PER-CONTROL LIMITS BEAT THE DECLARED RANGE, and the difference is not small.
  // `rig_range` is the control space's nominal domain ([0,1] for eyeline-v10c),
  // but the training data occupies a far narrower, per-control box inside it: 76
  // of that bundle's 168 controls never exceed 0.5 in any of its 246 poses, 35
  // never move at all, and the median per-control trained maximum is 0.622. So a
  // single ARKit channel at 1.0 can sit MULTIPLES past anything the decoders
  // saw — `MouthUpperUpLeft` drives `CTRL_L_mouth_upperLipRaise`, whose trained
  // max is 0.205 (nonzero in 9 of 246 rows), five times over; `MouthRollUpper`
  // drives `CTRL_R_mouth_lipsRollU`, trained max 0.143, seven times over. The
  // decoders extrapolate, and that is the blown-out, exploding face.
  //
  // The raw rig board has clamped to these limits since ENG-946 (it is why
  // dragging its sliders does not blow up); the ARKit path did not, so the same
  // pose reached the same decoders unclamped depending only on which UI produced
  // it. Same `trainedLimits`, so the two agree by construction.
  //
  // Live speech is barely affected: measured over real Convorcher frames, ~2.9 of
  // 168 controls exceed the unpadded envelope at all, by at most 0.076 — against
  // the 5-7x overshoot above.
  const perControl = Array.isArray(range) && Array.isArray(range[0])
  const lo0 = !perControl && range ? range[0] : -Infinity
  const hi0 = !perControl && range ? range[1] : Infinity
  for (let i = 0; i < gather.length; i++) {
    const v = base[i] + buf[gather[i]]
    const lim = perControl ? range[i] : null
    const lo = lim ? lim[0] : lo0
    const hi = lim ? lim[1] : hi0
    outN[i] = v < lo ? lo : v > hi ? hi : v
  }
  return outN
}
